/**
 * Arena ↔ Event competitive state projection (ADR-031 P3).
 * Storage is Event-owned subcollections; managed* arrays are read projections only.
 */

import * as tournamentEventRepository from "../repositories/tournamentEventRepository";
import * as tournamentEntryRepository from "../repositories/tournamentEntryRepository";
import * as tournamentDrawRepository from "../repositories/tournamentDrawRepository";
import * as tournamentFixtureRepository from "../repositories/tournamentFixtureRepository";
import * as arenaRepository from "../repositories/arenaRepository";
import * as tournamentModuleRepository from "../repositories/tournamentModuleRepository";
import { createManagedEventSummary } from "../domain/event/eventTypes";
import { resolveEventCompetition, resolveEventDisplayName } from "../domain/event/eventCompetition";

/**
 * Assemble ManagedDraw[] from Event draws + fixtures.
 * @param {object[]} draws
 * @param {object[]} fixtures
 */
export function assembleManagedDraws(draws, fixtures) {
  const byDraw = new Map();
  for (const fx of fixtures || []) {
    const key = fx.drawId;
    if (!key) continue;
    if (!byDraw.has(key)) byDraw.set(key, []);
    byDraw.get(key).push(fx);
  }
  return (draws || []).map((d) => ({
    ...d,
    competitionId: d.competitionId || d.arenaId,
    fixtures: (byDraw.get(d.drawId) || []).map((f) => {
      const { arenaId: _a, updatedAt: _u, createdAt: _c, ...rest } = f;
      return rest;
    }),
  }));
}

/**
 * Event docs → ManagedEventSummary[] for existing UI/domain helpers.
 * @param {object[]} events
 */
export function toManagedEventSummaries(events) {
  return (events || []).map((e) => ({
    eventId: e.eventId,
    name: resolveEventDisplayName(e),
    display: e.display || { name: resolveEventDisplayName(e) },
    competition: resolveEventCompetition(e),
    status: e.status || "draft",
    createdAtMs: e.createdAtMs || null,
  }));
}

/**
 * Load Event-owned competitive state and project onto Arena for consumers.
 * If Event subcollections are empty but legacy managed* exist, migrate once (pre-launch).
 * @param {object} arena
 * @param {{ fromServer?: boolean }} [opts]
 */
export async function hydrateCompetitiveState(arena, opts = {}) {
  if (!arena) return null;
  const arenaId = arena.arenaId || arena.competitionId || arena.id;
  if (!arenaId) return arena;

  let events = [];
  try {
    events = await tournamentEventRepository.listEvents(arenaId);
  } catch {
    return arena;
  }

  if (!events.length) {
    const planned = Array.isArray(arena.plannedEventNames)
      ? arena.plannedEventNames.map((n) => String(n || "").trim()).filter(Boolean)
      : [];
    const legacyEvents = Array.isArray(arena.managedEvents) ? arena.managedEvents : [];
    const legacyEntries = Array.isArray(arena.managedEntries) ? arena.managedEntries : [];
    const legacyDraws = Array.isArray(arena.managedDraws) ? arena.managedDraws : [];

    if (planned.length) {
      try {
        const { seedEventsFromNames } = await import("./tournamentEventService");
        events = await seedEventsFromNames(
          arenaId,
          planned,
          arena.createdBy || arena.organizerUid || "organizer"
        );
      } catch {
        /* fall through to embedded managedEvents */
      }
    }

    if (!events.length && legacyEvents.length) {
      try {
        await migrateEmbeddedCompetitiveState(arenaId, {
          ...arena,
          managedEvents: legacyEvents,
          managedEntries: legacyEntries,
          managedDraws: legacyDraws,
        });
        events = await tournamentEventRepository.listEvents(arenaId);
      } catch {
        return {
          ...arena,
          managedEvents: legacyEvents,
          managedEntries: legacyEntries,
          managedDraws: legacyDraws,
          __competitiveSource: "legacy_embedded",
        };
      }
    }

    if (!events.length && legacyEvents.length) {
      return {
        ...arena,
        managedEvents: legacyEvents,
        managedEntries: legacyEntries,
        managedDraws: legacyDraws,
        __competitiveSource: "arena_mirror",
      };
    }

    if (!events.length) {
      return {
        ...arena,
        managedEvents: [],
        managedEntries: [],
        managedDraws: [],
        __competitiveSource: "event_subcollections",
      };
    }
  }

  const eventIds = events.map((e) => e.eventId).filter(Boolean);
  let entries = [];
  let draws = [];
  let fixtures = [];
  try {
    const entryRead = () =>
      tournamentEntryRepository.listEntriesForArena(arenaId, eventIds, {
        fromServer: Boolean(opts.fromServer),
      });
    [entries, draws, fixtures] = await Promise.all([
      opts.fromServer
        ? entryRead().catch(() =>
            tournamentEntryRepository.listEntriesForArena(arenaId, eventIds)
          )
        : entryRead(),
      tournamentDrawRepository.listDrawsForArena(arenaId, eventIds),
      tournamentFixtureRepository.listFixturesForArena(arenaId, eventIds),
    ]);
  } catch {
    /* Event docs still project even if child reads fail */
  }

  const managedDraws = assembleManagedDraws(draws, fixtures);
  const managedEvents = toManagedEventSummaries(events);

  return {
    ...arena,
    managedEvents,
    managedEntries: entries,
    managedDraws,
    eventId: arena.eventId || managedEvents[0]?.eventId || null,
    entryCount: entries.length,
    __competitiveSource: "event_subcollections",
  };
}

/**
 * One-time pre-launch move of embedded arrays into Event subcollections.
 * @param {string} arenaId
 * @param {object} arena
 */
export async function migrateEmbeddedCompetitiveState(arenaId, arena) {
  const legacyEvents = Array.isArray(arena.managedEvents) ? arena.managedEvents : [];
  const legacyEntries = Array.isArray(arena.managedEntries) ? arena.managedEntries : [];
  const legacyDraws = Array.isArray(arena.managedDraws) ? arena.managedDraws : [];

  let events = legacyEvents;
  if (!events.length && (legacyEntries.length || legacyDraws.length)) {
    const shell = createManagedEventSummary({
      name: arena.name || "Main event",
      createdBy: arena.createdBy || "system",
    });
    events = [shell];
  }

  for (const ev of events) {
    await tournamentEventRepository.upsertEvent(arenaId, {
      ...ev,
      arenaId,
      name: ev.name || "Event",
      status: ev.status || "draft",
    });
  }

  const eventIds = events.map((e) => e.eventId);
  const fallbackEventId = eventIds[0] || null;

  const entriesByEvent = new Map(eventIds.map((id) => [id, []]));
  for (const entry of legacyEntries) {
    const eventId = entry.eventId && eventIds.includes(entry.eventId)
      ? entry.eventId
      : fallbackEventId;
    if (!eventId) continue;
    if (!entriesByEvent.has(eventId)) entriesByEvent.set(eventId, []);
    entriesByEvent.get(eventId).push({ ...entry, eventId });
  }
  for (const [eventId, list] of entriesByEvent) {
    await tournamentEntryRepository.replaceEntriesForEvent(arenaId, eventId, list);
  }

  for (const draw of legacyDraws) {
    const eventId =
      draw.eventId && eventIds.includes(draw.eventId) ? draw.eventId : fallbackEventId;
    if (!eventId || !draw.drawId) continue;
    const fixtures = Array.isArray(draw.fixtures) ? draw.fixtures : [];
    await tournamentDrawRepository.upsertDraw(arenaId, eventId, {
      ...draw,
      eventId,
      competitionId: arenaId,
    });
    await tournamentFixtureRepository.replaceFixturesForDraw(
      arenaId,
      eventId,
      draw.drawId,
      fixtures.map((f) => ({ ...f, drawId: draw.drawId, eventId }))
    );
  }

  await syncEventRegistry(arenaId, eventIds);

  // Strip competitive arrays from Arena parent (Arena must not own tournament state)
  await arenaRepository.updateCompetition(arenaId, {
    managedEvents: [],
    managedEntries: [],
    managedDraws: [],
    eventId: fallbackEventId,
    entryCount: legacyEntries.length,
    updatedAtMs: Date.now(),
  });
}

/**
 * Keep Tournament Module.eventIds in sync (registry only).
 * @param {string} arenaId
 * @param {string[]} eventIds
 */
export async function syncEventRegistry(arenaId, eventIds) {
  const ids = Array.isArray(eventIds) ? [...new Set(eventIds.filter(Boolean))] : [];
  try {
    const mod = await tournamentModuleRepository.fetchTournamentModule(arenaId);
    if (!mod) return;
    await tournamentModuleRepository.setTournamentModule(arenaId, {
      ...mod,
      eventIds: ids,
      updatedAtMs: Date.now(),
    });
  } catch {
    /* module may not exist on casual arenas */
  }
  try {
    await arenaRepository.updateCompetition(arenaId, {
      eventId: ids[0] || null,
      updatedAtMs: Date.now(),
    });
  } catch {
    /* ignore */
  }
}

/**
 * Persist full entry list for an Arena (grouped by eventId).
 * Organizer / manager path — rewrites all entries per event.
 * @param {string} arenaId
 * @param {object[]} managedEntries
 */
export async function persistArenaEntries(arenaId, managedEntries) {
  const events = await tournamentEventRepository.listEvents(arenaId);
  let eventIds = events.map((e) => e.eventId);
  if (!eventIds.length) {
    const fromEntries = [
      ...new Set((managedEntries || []).map((e) => e.eventId).filter(Boolean)),
    ];
    for (const eventId of fromEntries) {
      await tournamentEventRepository.upsertEvent(arenaId, {
        eventId,
        arenaId,
        name: "Event",
        status: "draft",
        createdAtMs: Date.now(),
      });
    }
    eventIds = fromEntries;
  }
  const fallback = eventIds[0];
  const byEvent = new Map(eventIds.map((id) => [id, []]));
  for (const entry of managedEntries || []) {
    const eventId =
      entry.eventId && byEvent.has(entry.eventId) ? entry.eventId : fallback;
    if (!eventId) continue;
    byEvent.get(eventId).push({ ...entry, eventId, competitionId: arenaId });
  }
  for (const [eventId, list] of byEvent) {
    await tournamentEntryRepository.replaceEntriesForEvent(arenaId, eventId, list);
  }
  await arenaRepository.updateCompetition(arenaId, {
    entryCount: (managedEntries || []).length,
    managedEntries: [],
    updatedAtMs: Date.now(),
  });
}

/**
 * Self-registration path — write only this player's entry.
 * The arena document is left alone so hundreds of registrations do not
 * queue on one parent write (that write used to drop or stall entries).
 * @param {string} arenaId
 * @param {object} entry
 * @param {{ entryCount?: number }} [_opts]
 */
export async function persistOwnArenaEntry(arenaId, entry, _opts = {}) {
  if (!arenaId || !entry?.entryId || !entry?.eventId) {
    throw new Error("arenaId, entryId, and eventId required.");
  }
  return tournamentEntryRepository.claimEntry(arenaId, entry.eventId, {
    ...entry,
    eventId: entry.eventId,
    competitionId: arenaId,
  });
}

/**
 * Organizer add/edit — upsert these entries and leave every other entry in place.
 * @param {string} arenaId
 * @param {object[]} entries
 */
export async function persistArenaEntryUpserts(arenaId, entries) {
  const saved = [];
  for (const entry of entries || []) {
    if (!entry?.entryId || !entry?.eventId) continue;
    saved.push(
      await tournamentEntryRepository.upsertEntry(arenaId, entry.eventId, {
        ...entry,
        eventId: entry.eventId,
        competitionId: arenaId,
      })
    );
  }
  return saved;
}

/**
 * @param {string} arenaId
 * @param {object} entry
 */
export async function persistArenaEntryRemoval(arenaId, entry) {
  if (!arenaId || !entry?.eventId || !entry?.entryId) return;
  await tournamentEntryRepository.deleteEntry(arenaId, entry.eventId, entry.entryId);
}

/**
 * Persist full draw list (writes draws + fixtures under each Event).
 * @param {string} arenaId
 * @param {object[]} managedDraws
 * @param {Record<string, unknown>} [arenaExtra] e.g. opsTimeline
 */
export async function persistArenaDraws(arenaId, managedDraws, arenaExtra = {}) {
  const events = await tournamentEventRepository.listEvents(arenaId);
  let eventIds = events.map((e) => e.eventId);
  const draws = managedDraws || [];

  for (const draw of draws) {
    if (draw.eventId && !eventIds.includes(draw.eventId)) {
      await tournamentEventRepository.upsertEvent(arenaId, {
        eventId: draw.eventId,
        arenaId,
        name: "Event",
        status: "draft",
        createdAtMs: Date.now(),
      });
      eventIds = [...eventIds, draw.eventId];
    }
  }
  const fallback = eventIds[0];

  const existingDraws = await tournamentDrawRepository.listDrawsForArena(arenaId, eventIds);
  const nextIds = new Set(draws.map((d) => d.drawId));

  for (const draw of draws) {
    const eventId =
      draw.eventId && eventIds.includes(draw.eventId) ? draw.eventId : fallback;
    if (!eventId || !draw.drawId) continue;
    const fixtures = Array.isArray(draw.fixtures) ? draw.fixtures : [];
    await tournamentDrawRepository.upsertDraw(arenaId, eventId, {
      ...draw,
      eventId,
      competitionId: arenaId,
    });
    await tournamentFixtureRepository.replaceFixturesForDraw(
      arenaId,
      eventId,
      draw.drawId,
      fixtures.map((f) => ({ ...f, drawId: draw.drawId, eventId }))
    );
  }

  for (const old of existingDraws) {
    if (nextIds.has(old.drawId)) continue;
    const fixtures = await tournamentFixtureRepository.listFixturesForEvent(
      arenaId,
      old.eventId,
      { drawId: old.drawId }
    );
    await Promise.all(
      fixtures.map((f) =>
        tournamentFixtureRepository.deleteFixture(arenaId, old.eventId, f.fixtureId)
      )
    );
    await tournamentDrawRepository.deleteDraw(arenaId, old.eventId, old.drawId);
  }

  const { managedDraws: _drop, ...extra } = arenaExtra;
  await arenaRepository.updateCompetition(arenaId, {
    ...extra,
    managedDraws: [],
    updatedAtMs: Date.now(),
  });
}
